import { useId, useState } from 'react';
import type React from 'react';
import { ArrowRight, Check, CheckCircle2, FileText, Lock, RefreshCw } from 'lucide-react';
import { Aufklapper, GefuehrtRahmen, Handlungen, Kopf, Kreisel } from '../../common/gefuehrt/GefuehrtRahmen';
import { FELD, FOKUS, KNOPF_HAUPT, KNOPF_LEISE, KNOPF_WAHL, WAHL_AUS, WAHL_GROSS } from '../../common/gefuehrt/klassen';
import type { OffeneFrage, Zusammenfassung } from './dialogLogik';
import { DIALOG_ABSCHNITT_LABEL } from './dialogLogik';
import type {
    AnlegenKarteProps, AutomatikKarteProps, DialogRahmenProps, Eingaben, ErlassenderKarteProps, FreigabeKarteProps,
    FreigegebenKarteProps, GruppeKarteProps, HindernisKarteProps, KiLiestKarteProps, KlaerungKarteProps,
    NeuDazuKarteProps, NurAnsichtKarteProps, UebersichtKarteProps, VorgabenKarteProps,
} from './dialogKartenTypen';

/**
 * Die Karten des gefuehrten Dialogs der Baustellenordnung — reine Darstellung
 * nach dem Vertrag in `dialogKartenTypen.ts`. Jede Karte nennt EINE Aufgabe:
 * hoechstens ein Satz Erklaerung, genau ein grosser Hauptknopf, hoechstens
 * zwei leise Textknoepfe (Regeln im Kopf von `GefuehrtRahmen.tsx`).
 *
 * Eigener Zustand ist nur Eingabe (Felder, Haken). Welche Karte dran ist und
 * was ein Klick schreibt, entscheidet `BauordnungDialog.tsx`.
 *
 * Alltagssprache wie in der Planungsphase: „Wer erlässt die Ordnung?", nicht
 * „Erlassender (Rolle)". Jede Karte sagt, was die App schon getan hat und was
 * der Klick tut.
 */

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

const anzahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

/** ISO → TT.MM.JJJJ durch Zerlegen; kein `new Date`, der Tag soll nicht wandern. */
const tagDeutsch = (iso?: string | null): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '').trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/** Der letzte Schreibfehler — rot, als Hinweis vorgelesen. */
const FehlerZeile: React.FC<{ fehler: string | null }> = ({ fehler }) => (fehler
    ? <p role="alert" className="mt-3 text-sm font-bold text-rose-700 leading-snug break-words">{fehler}</p>
    : null);

/** Ein amberfarbener Satz (KI fehlt, Sperrgrund). */
const HinweisZeile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="mt-3 text-sm text-amber-700 leading-snug break-words">{children}</p>
);

const TON: Record<'ok' | 'teils' | 'fehler', string> = {
    ok: 'text-emerald-800',
    teils: 'text-amber-800',
    fehler: 'text-rose-700',
};

/** „9 Kapitel, 23 Regelungen, 4 Rollen" — so steht der Stand auf mehreren Karten. */
const standSatz = (z: Zusammenfassung): string =>
    `${anzahl(z.kapitel, 'Kapitel', 'Kapitel')}, ${anzahl(z.regelungen, 'Regelung', 'Regelungen')}, ${anzahl(z.rollen, 'Rolle', 'Rollen')}`;

/** Der kleine Textknopf an einer Zeile („Regelung weglassen", „Stimmt nicht"). */
const ZEILEN_KNOPF = `min-h-8 rounded text-xs font-bold text-slate-400 hover:text-slate-700 disabled:opacity-40 ${FOKUS}`;

const weglassenText = (f: OffeneFrage): string =>
    (f.regeln.length > 1 ? `${f.regeln.length} Regelungen weglassen` : 'Regelung weglassen');

/**
 * Ein Feld je Frage: die Frage als Beschriftung, der Hinweis klein darunter,
 * „Regelung weglassen" leise daneben. Gemeinsam fuer Vorgaben- und Gruppenkarte.
 */
const FrageFeld: React.FC<{
    frage: OffeneFrage;
    wert: string;
    laeuft: boolean;
    onWert: (wert: string) => void;
    onWeglassen: () => void;
}> = ({ frage, wert, laeuft, onWert, onWeglassen }) => {
    const id = useId();
    const hinweisId = `${id}-hinweis`;
    const hinweis = frage.definition.hinweis;
    return (
        <li className="py-3">
            <label htmlFor={id} className="block text-sm font-bold text-slate-800 leading-snug break-words">
                {frage.definition.frage}
            </label>
            {hinweis && <p id={hinweisId} className="mt-0.5 text-xs text-slate-500 leading-snug break-words">{hinweis}</p>}
            <input
                id={id}
                type="text"
                value={wert}
                onChange={e => onWert(e.target.value)}
                aria-describedby={hinweis ? hinweisId : undefined}
                autoComplete="off"
                className={`mt-1.5 ${FELD}`}
            />
            <div className="mt-0.5 flex justify-end">
                <button type="button" onClick={() => onWeglassen()} disabled={laeuft} className={ZEILEN_KNOPF}>
                    {weglassenText(frage)}
                </button>
            </div>
        </li>
    );
};

/** Eingaben einer Feldkarte: nur Geaendertes im Zustand, sonst die Vorbelegung. */
const useEingaben = (vorbelegung: (f: OffeneFrage) => string) => {
    const [geaendert, setGeaendert] = useState<Eingaben>({});
    const wert = (f: OffeneFrage): string => geaendert[f.verweis] ?? vorbelegung(f);
    const setze = (f: OffeneFrage, w: string) => setGeaendert(alt => ({ ...alt, [f.verweis]: w }));
    const alle = (fragen: readonly OffeneFrage[]): Eingaben => {
        const e: Eingaben = {};
        for (const f of fragen) {
            const w = wert(f).trim();
            if (w) e[f.verweis] = w;
        }
        return e;
    };
    return { wert, setze, alle };
};

// ═══════════════════════════════════════════════════════════════════════════
// RAHMEN
// ═══════════════════════════════════════════════════════════════════════════

/** Rahmen des Dialogs: die drei Abschnitte auf die Schritte des gemeinsamen Rahmens abgebildet. */
export const DialogRahmen: React.FC<DialogRahmenProps> = ({ fortschritt, children, fuss }) => (
    <GefuehrtRahmen
        schritte={fortschritt.map(({ abschnitt, stand }) => ({ label: DIALOG_ABSCHNITT_LABEL[abschnitt], stand }))}
        fuss={fuss}
    >
        {children}
    </GefuehrtRahmen>
);

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „ENTWURF"
// ═══════════════════════════════════════════════════════════════════════════

/** Noch kein Kopfsatz — ein Klick legt ihn an, den Rest baut die App. */
export const AnlegenKarte: React.FC<AnlegenKarteProps> = ({ nurAnsicht, laeuft, fehler, onAnlegen }) => (
    <>
        <Kopf
            titel="Für dieses Projekt gibt es noch keine Baustellenordnung"
            zeile={nurAnsicht
                ? 'Das Projekt ist nur zur Ansicht geöffnet.'
                : 'Ein Klick legt sie an. Den Entwurf baut die App danach aus Planungsphase, Handbuch und Katalog.'}
        />
        <FehlerZeile fehler={fehler} />
        {!nurAnsicht && (
            <Handlungen>
                <button type="button" onClick={() => onAnlegen()} disabled={laeuft} className={KNOPF_HAUPT}>
                    {laeuft && <Kreisel klein />} Anlegen
                </button>
            </Handlungen>
        )}
    </>
);

/** Der Entwurf baut sich (von selbst); nach einem Fehlschlag „Erneut versuchen". */
export const AutomatikKarte: React.FC<AutomatikKarteProps> = ({ fehlgeschlagen, fehler, onErneut, onOhne }) => {
    if (!fehlgeschlagen) {
        return (
            <Kopf
                rolle="status"
                icon={<Kreisel />}
                titel="Der Entwurf wird gebaut …"
                zeile="Aus Planungsphase, Handbuch, Notrufplan und dem Katalog der Textbausteine."
            />
        );
    }
    return (
        <>
            <Kopf rolle="alert" titel="Der Entwurf konnte nicht gebaut werden" zeile="Beim nächsten Öffnen versucht es die App erneut." />
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onErneut()} className={KNOPF_HAUPT}>
                    <RefreshCw className="w-5 h-5" /> Erneut versuchen
                </button>
                <button type="button" onClick={() => onOhne()} className={KNOPF_LEISE}>
                    Ohne Automatik weiter
                </button>
            </Handlungen>
        </>
    );
};

/** Die KI liest den Entwurf (startet von selbst, einmal je Fassung). */
export const KiLiestKarte: React.FC<KiLiestKarteProps> = ({ onAbbrechen }) => (
    <>
        <Kopf
            rolle="status"
            icon={<Kreisel />}
            titel="Die KI liest den Entwurf …"
            zeile="Sie ergänzt, was Katalog und Handbuch nicht hergeben. Übernommen wird erst nach Ihrem „Stimmt so“."
        />
        <p className="mt-3 text-xs text-slate-500 leading-snug">
            Dazu gehen Kapitel, bestätigte Regelungen und die Merkmale an die KI (Google Gemini), ohne Namen, Firmen,
            Anschriften und Zahlen aus dem Projekt.
        </p>
        <Handlungen>
            <button type="button" onClick={() => onAbbrechen()} className={KNOPF_LEISE}>
                Abbrechen
            </button>
        </Handlungen>
    </>
);

const herkunftText = (h: { katalog: number; handbuch: number; ki: number; frei: number }): string =>
    [
        h.katalog ? `${h.katalog} Katalog` : '',
        h.handbuch ? `${h.handbuch} Handbuch` : '',
        h.ki ? `${h.ki} KI` : '',
        h.frei ? `${h.frei} von Hand` : '',
    ].filter(Boolean).join(' · ');

/** „Das steht schon drin" — was die Automatik geschrieben hat, darunter die KI-Ergänzungen, alle vorausgewählt. */
export const UebersichtKarte: React.FC<UebersichtKarteProps> = ({ uebersicht: u, laeuft, fehler, onUmschalten, onStimmtSo }) => {
    const kiAnzahl = u.ki.reduce((n, g) => n + g.zeilen.length, 0);
    const kiTitelId = useId();
    const z = u.zusammen;
    const offen = [
        z.offenePunkte ? anzahl(z.offenePunkte, 'offener Punkt', 'offene Punkte') : '',
        u.zuKlaeren ? `davon ${anzahl(u.zuKlaeren, 'Merkmal', 'Merkmale')} zu klären` : '',
    ].filter(Boolean).join(', ');
    return (
        <>
            <Kopf
                titel="Das steht schon drin"
                zeile={`${standSatz(z)}${offen ? `; ${offen}` : ''}.`}
            />
            <ul className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                {u.kapitel.map(k => (
                    <li key={k.kapitel} className="py-2 flex items-baseline justify-between gap-3">
                        <span className="min-w-0 text-sm text-slate-800 leading-snug break-words">{k.titel}</span>
                        <span className="shrink-0 text-right text-xs text-slate-500 tabular-nums">
                            {k.bestaetigt
                                ? <><span className="font-black text-slate-800">{k.bestaetigt}</span> · {herkunftText(k.herkunft)}</>
                                : 'noch leer'}
                        </span>
                    </li>
                ))}
            </ul>
            {u.weggelassen.length > 0 && (
                <Aufklapper label={`Was weggelassen wurde (${u.weggelassen.length})`}>
                    <ul className="space-y-1">
                        {u.weggelassen.map((w, i) => (
                            <li key={`${w.titel}-${i}`}>
                                <span className="font-bold text-slate-700">{w.titel}</span>: {w.grund}
                            </li>
                        ))}
                    </ul>
                </Aufklapper>
            )}
            {kiAnzahl > 0 && (
                <div className="mt-5">
                    <p id={kiTitelId} className="text-sm font-black text-slate-900">
                        {anzahl(kiAnzahl, 'Ergänzung', 'Ergänzungen')} der KI. Stimmt das so?
                    </p>
                    <div aria-labelledby={kiTitelId} className="mt-2 space-y-3">
                        {u.ki.map(g => (
                            <div key={g.kapitel}>
                                <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">{g.titel}</p>
                                <ul className="divide-y divide-slate-100">
                                    {g.zeilen.map(zeile => (
                                        <li key={zeile.id} className="py-2 flex items-start gap-3">
                                            <span className={`min-w-0 flex-1 text-sm leading-snug break-words ${zeile.verworfen ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                {zeile.wortlaut}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => onUmschalten(zeile.id)}
                                                aria-pressed={zeile.verworfen}
                                                disabled={laeuft}
                                                className={`shrink-0 ${ZEILEN_KNOPF}`}
                                            >
                                                {zeile.verworfen ? 'Doch übernehmen' : 'Stimmt nicht'}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {u.kiHinweis && <HinweisZeile>{u.kiHinweis}</HinweisZeile>}
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onStimmtSo()} disabled={laeuft} className={KNOPF_HAUPT}>
                    {laeuft ? <Kreisel klein /> : kiAnzahl ? <Check className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                    {kiAnzahl ? 'Stimmt so' : 'Weiter'}
                </button>
            </Handlungen>
        </>
    );
};

/** Seit dem letzten Lauf trifft mehr zu (Planungsphase nachgezogen) — nicht still schreiben, sondern zeigen. */
export const NeuDazuKarte: React.FC<NeuDazuKarteProps> = ({ zeilen, laeuft, fehler, onStimmtSo, onWeglassen, onSpaeter }) => (
    <>
        <Kopf
            titel={zeilen.length === 1 ? 'Eine Regelung kommt dazu' : `${zeilen.length} Regelungen kommen dazu`}
            zeile="Die Angaben zum Projekt haben sich geändert. Das trifft jetzt zusätzlich zu."
        />
        <ul className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
            {zeilen.map(z => (
                <li key={z.bausteinId} className="py-2.5">
                    <p className="text-sm font-bold text-slate-800 leading-snug break-words">{z.titel}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{z.kapitelTitel} · {z.grund}</p>
                    <p className="mt-1 text-sm text-slate-600 leading-snug break-words">{z.wortlaut}</p>
                </li>
            ))}
        </ul>
        <FehlerZeile fehler={fehler} />
        <Handlungen>
            <button type="button" onClick={() => onStimmtSo()} disabled={laeuft} className={KNOPF_HAUPT}>
                {laeuft ? <Kreisel klein /> : <Check className="w-5 h-5" />} Stimmt so
            </button>
            <button type="button" onClick={() => onWeglassen()} disabled={laeuft} className={KNOPF_LEISE}>
                Weglassen
            </button>
            <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                Später
            </button>
        </Handlungen>
    </>
);

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „OFFENE PUNKTE"
// ═══════════════════════════════════════════════════════════════════════════

/** Bauherr UND Dritter sind erfasst — wer erlaesst, regelt der Vertrag, nicht die App. */
export const ErlassenderKarte: React.FC<ErlassenderKarteProps> = ({ wahl, laeuft, fehler, onWaehle, onAndere }) => {
    const titelId = useId();
    return (
        <>
            <Kopf
                id={titelId}
                gross
                titel="Wer erlässt die Baustellenordnung?"
                zeile="Wer das Hausrecht hat, steht im Vertrag. Die App rät hier nicht."
            />
            <div role="group" aria-labelledby={titelId} className="mt-5 grid gap-3 sm:grid-cols-2">
                {wahl.map(w => (
                    <button
                        key={w.id}
                        type="button"
                        onClick={() => onWaehle(w)}
                        disabled={laeuft}
                        className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} flex-col text-base`}
                    >
                        <span>{w.firma}</span>
                        <span className="mt-0.5 text-xs font-medium text-slate-500">{w.rolle}</span>
                    </button>
                ))}
            </div>
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onAndere()} className={KNOPF_LEISE}>
                    Jemand anderes
                </button>
            </Handlungen>
        </>
    );
};

/** Ein Merkmal ist in der Planungsphase offen — Ja nimmt die Regelungen auf, Nein laesst sie weg. */
export const KlaerungKarte: React.FC<KlaerungKarteProps> = ({ frage, kurz, bausteine, offen, laeuft, fehler, onAntwort, onWeissNicht }) => {
    const titelId = useId();
    return (
        <>
            <Kopf
                id={titelId}
                gross
                ueber={offen > 1 ? `Merkmal klären · noch ${offen}` : 'Merkmal klären'}
                titel={frage}
                zeile="Ja nimmt die passenden Regelungen auf, Nein lässt sie weg. Die Planungsphase bleibt unverändert."
            />
            {bausteine.length > 0 && (
                <Aufklapper label={`Was an „${kurz}“ hängt (${bausteine.length})`}>
                    <ul className="ml-5 list-disc space-y-0.5">
                        {bausteine.map((b, i) => <li key={`${b}-${i}`}>{b}</li>)}
                    </ul>
                </Aufklapper>
            )}
            <div role="group" aria-labelledby={titelId} className="mt-5 grid grid-cols-2 gap-3">
                <button type="button" onClick={() => onAntwort(true)} disabled={laeuft} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-lg`}>
                    Ja
                </button>
                <button type="button" onClick={() => onAntwort(false)} disabled={laeuft} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-lg`}>
                    Nein
                </button>
            </div>
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onWeissNicht()} className={KNOPF_LEISE}>
                    Weiß nicht
                </button>
            </Handlungen>
        </>
    );
};

/** Alle Verantwortlichen und Stellen mit Vorschlag auf einer Karte — ein „Stimmt so" für alle. */
export const VorgabenKarte: React.FC<VorgabenKarteProps> = ({ fragen, laeuft, fehler, onStimmtSo, onWeglassen, onSpaeter }) => {
    const e = useEingaben(f => f.definition.vorgabe ?? '');
    return (
        <>
            <Kopf
                ueber={`Offene Punkte · Zuständigkeiten (${fragen.length})`}
                titel="Wer ist wofür zuständig?"
                zeile="Vorgeschlagen ist, was auf den meisten Baustellen gilt. Ändern Sie nur, was hier anders ist."
            />
            <ul className="mt-3 divide-y divide-slate-100">
                {fragen.map(f => (
                    <FrageFeld
                        key={f.verweis}
                        frage={f}
                        wert={e.wert(f)}
                        laeuft={laeuft}
                        onWert={w => e.setze(f, w)}
                        onWeglassen={() => onWeglassen(f)}
                    />
                ))}
            </ul>
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onStimmtSo(e.alle(fragen))} disabled={laeuft} className={KNOPF_HAUPT}>
                    {laeuft ? <Kreisel klein /> : <Check className="w-5 h-5" />} Stimmt so
                </button>
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

const GRUPPE_TITEL: Record<string, string> = {
    verantwortliche: 'Wer ist wofür zuständig?',
    organisation: 'Wer macht was auf der Baustelle?',
    notfall: 'Was gilt im Notfall?',
    zeiten: 'Welche Zeiten gelten?',
    orte: 'Wo ist was auf der Baustelle?',
    regelung: 'Was legt der Auftraggeber fest?',
    projekt: 'Was fehlt noch zum Projekt?',
};

/** Eine Gruppe offener Punkte (Notfall, Zeiten, Orte …) — mehrere Felder, ein „Weiter". */
export const GruppeKarte: React.FC<GruppeKarteProps> = ({
    gruppe, label, fragen, vorbelegung, laeuft, fehler, onWeiter, onWeglassen, onSpaeter,
}) => {
    const e = useEingaben(f => vorbelegung[f.verweis] ?? '');
    const ausgefuellt = e.alle(fragen);
    const leer = Object.keys(ausgefuellt).length === 0;
    return (
        <>
            <Kopf
                ueber={`Offene Punkte · ${label} (${fragen.length})`}
                titel={GRUPPE_TITEL[gruppe] ?? label}
                zeile="Was Sie eintragen, steht so im Dokument. Leere Felder bleiben offen."
            />
            <ul className="mt-3 divide-y divide-slate-100">
                {fragen.map(f => (
                    <FrageFeld
                        key={f.verweis}
                        frage={f}
                        wert={e.wert(f)}
                        laeuft={laeuft}
                        onWert={w => e.setze(f, w)}
                        onWeglassen={() => onWeglassen(f)}
                    />
                ))}
            </ul>
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button type="button" onClick={() => onWeiter(ausgefuellt)} disabled={laeuft || leer} className={KNOPF_HAUPT}>
                    {laeuft ? <Kreisel klein /> : <ArrowRight className="w-5 h-5" />} Weiter
                </button>
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

const BEREICH_LABEL: Record<string, string> = {
    grundlagen: 'Grundlagen',
    kapitel: 'Kapitel & Texte',
    rollen: 'Organisation',
    offene_punkte: 'Offene Punkte',
};

/** Ein Feld des Kopfsatzes auf der Hinderniskarte. */
const KopfFeld: React.FC<{ label: string; wert: string; pflicht: boolean; onWert: (w: string) => void }> = ({ label, wert, pflicht, onWert }) => {
    const id = useId();
    return (
        <div className="mt-3">
            <label htmlFor={id} className="block text-sm font-bold text-slate-800">
                {label}{!pflicht && <span className="font-medium text-slate-400"> (optional)</span>}
            </label>
            <input id={id} type="text" value={wert} onChange={ev => onWert(ev.target.value)} autoComplete="off" className={`mt-1 ${FELD}`} />
        </div>
    );
};

/**
 * Was der Freigabe noch entgegensteht: Erlassender und Unterzeichner direkt
 * als Felder, Uebersprungenes zum Wieder-Zeigen, alles andere mit Satz und
 * dem Weg nach „Alle Bereiche".
 */
export const HindernisKarte: React.FC<HindernisKarteProps> = ({
    kopf, uebersprungen, rest, kannWiederZeigen, laeuft, fehler, onKopf, onAlleBereiche, onWiederZeigen,
}) => {
    const [firma, setFirma] = useState(kopf?.firma.wert ?? '');
    const [name, setName] = useState(kopf?.name.wert ?? '');
    const [funktion, setFunktion] = useState(kopf?.funktion.wert ?? '');
    const sichtbar = rest.slice(0, 5);
    const zielBereich = rest[0]?.bereich ?? 'grundlagen';
    const kopfFehlt = !!kopf && ((kopf.firma.zeigen && !firma.trim()) || (kopf.name.zeigen && !name.trim()));

    const titel = kopf
        ? (kopf.firma.zeigen ? 'Wer erlässt die Baustellenordnung?' : 'Wer unterschreibt für den Erlassenden?')
        : 'Vor der Freigabe fehlt noch etwas';
    const zeile = kopf
        ? 'Ohne Erlassenden und Unterschrift hat die Ordnung keine Verbindlichkeit.'
        : 'Das lässt sich hier nicht beantworten. Es steht unter „Alle Bereiche“.';

    const wiederZeigen = uebersprungen > 0 && kannWiederZeigen;
    const haupt = kopf
        ? (
            <button
                type="button"
                onClick={() => onKopf({ firma: firma.trim(), name: name.trim(), funktion: funktion.trim() })}
                disabled={laeuft || kopfFehlt}
                className={KNOPF_HAUPT}
            >
                {laeuft ? <Kreisel klein /> : <ArrowRight className="w-5 h-5" />} Weiter
            </button>
        )
        : wiederZeigen
            ? (
                <button type="button" onClick={() => onWiederZeigen()} className={KNOPF_HAUPT}>
                    Offene Fragen zeigen
                </button>
            )
            : (
                <button type="button" onClick={() => onAlleBereiche(zielBereich)} className={KNOPF_HAUPT}>
                    In „Alle Bereiche“ öffnen <ArrowRight className="w-5 h-5" />
                </button>
            );

    return (
        <>
            <Kopf titel={titel} zeile={zeile} />
            {kopf && (
                <div className="mt-2">
                    {kopf.firma.zeigen && <KopfFeld label="Erlassender (Firma)" wert={firma} pflicht onWert={setFirma} />}
                    {kopf.name.zeigen && <KopfFeld label="Unterzeichner (Name)" wert={name} pflicht onWert={setName} />}
                    {kopf.funktion.zeigen && <KopfFeld label="Funktion des Unterzeichners" wert={funktion} pflicht={false} onWert={setFunktion} />}
                </div>
            )}
            {uebersprungen > 0 && (
                <p className="mt-4 text-sm text-slate-700 leading-snug">
                    {uebersprungen === 1 ? 'Eine Frage ist noch offen' : `${uebersprungen} Fragen sind noch offen`}. Sie haben sie übersprungen.
                </p>
            )}
            {sichtbar.length > 0 && (
                <ul className="mt-3 space-y-1">
                    {sichtbar.map((h, i) => (
                        <li key={`${h.bereich}-${i}`} className="text-sm text-slate-700 leading-snug break-words">
                            <span className="text-xs font-bold text-slate-400">{BEREICH_LABEL[h.bereich] ?? h.bereich}: </span>
                            {h.text}
                        </li>
                    ))}
                    {rest.length > sichtbar.length && (
                        <li className="text-xs text-slate-500">… und {rest.length - sichtbar.length} weitere.</li>
                    )}
                </ul>
            )}
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                {haupt}
                {kopf && wiederZeigen && (
                    <button type="button" onClick={() => onWiederZeigen()} className={KNOPF_LEISE}>
                        Offene Fragen zeigen
                    </button>
                )}
                {(kopf || wiederZeigen) && rest.length > 0 && (
                    <button type="button" onClick={() => onAlleBereiche(zielBereich)} className={KNOPF_LEISE}>
                        In „Alle Bereiche“ öffnen
                    </button>
                )}
            </Handlungen>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „FREIGABE"
// ═══════════════════════════════════════════════════════════════════════════

/** Alles vollstaendig: Fassung, PDF, Buchungshaken, „Freigeben" — Ablage, Journal und Buchung haengen daran. */
export const FreigabeKarte: React.FC<FreigabeKarteProps> = ({
    zusammen, fassung: vorbelegt, haken, sperrgrund, laeuft, fehler, onPdf, onFreigeben,
}) => {
    const fassungId = useId();
    const [fassung, setFassung] = useState(vorbelegt);
    const [buchen, setBuchen] = useState(haken.art === 'haken');
    return (
        <>
            <Kopf
                icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                titel="Alles vollständig und bereit zur Freigabe"
                zeile={`${standSatz(zusammen)}.`}
            />
            <div className="mt-4">
                <label htmlFor={fassungId} className="block text-sm font-bold text-slate-800">Fassung</label>
                <input
                    id={fassungId}
                    type="text"
                    value={fassung}
                    onChange={ev => setFassung(ev.target.value)}
                    autoComplete="off"
                    className={`mt-1 max-w-[14rem] ${FELD}`}
                />
            </div>
            {haken.art === 'haken' && (
                <label className="mt-4 flex min-h-11 cursor-pointer items-start gap-3 rounded-lg py-1">
                    <input
                        type="checkbox"
                        checked={buchen}
                        onChange={ev => setBuchen(ev.target.checked)}
                        className="mt-0.5 w-5 h-5 shrink-0 accent-blue-600"
                    />
                    <span className="text-sm text-slate-800 leading-snug">Leistung buchen: {haken.position.text}</span>
                </label>
            )}
            {haken.art === 'nachfrage' && (
                <p className="mt-4 text-sm text-slate-600 leading-snug">Die Abrechnung fragt nach der Freigabe nach, welche Position.</p>
            )}
            {zusammen.kiOffen > 0 && (
                <p className="mt-3 text-xs text-slate-500 leading-snug">
                    {anzahl(zusammen.kiOffen, 'Vorschlag', 'Vorschläge')} der KI {zusammen.kiOffen === 1 ? 'ist' : 'sind'} nicht
                    bestätigt und {zusammen.kiOffen === 1 ? 'steht' : 'stehen'} deshalb nicht im Dokument.
                </p>
            )}
            <p className="mt-3 text-xs text-slate-500 leading-snug">
                Mit der Freigabe wird der Stand eingefroren, im Projekt festgehalten und als PDF in der Projektablage abgelegt.
            </p>
            {sperrgrund && <HinweisZeile>{sperrgrund}</HinweisZeile>}
            <FehlerZeile fehler={fehler} />
            <Handlungen>
                <button
                    type="button"
                    onClick={() => onFreigeben(fassung.trim(), haken.art === 'haken' && buchen)}
                    disabled={laeuft || !!sperrgrund}
                    title={sperrgrund ?? undefined}
                    className={KNOPF_HAUPT}
                >
                    {laeuft ? <Kreisel klein /> : <Lock className="w-5 h-5" />} Freigeben
                </button>
                <button type="button" onClick={() => onPdf()} className={KNOPF_LEISE}>
                    <FileText className="w-4 h-4" /> PDF ansehen
                </button>
            </Handlungen>
        </>
    );
};

/** Freigegeben: Fassung, Ablage, Buchung — „Neue Fassung eröffnen" nur leise. */
export const FreigegebenKarte: React.FC<FreigegebenKarteProps> = ({
    fassung, am, von, dokumente, ablageLaeuft, ablageMeldung, gebuchtAm, buchungsMeldung, nurAnsicht, sperre,
    onNeueFassung, onBuchen,
}) => {
    const tag = tagDeutsch(am);
    const unterzeile = [tag ? `am ${tag}` : '', von ? `durch ${von}` : ''].filter(Boolean).join(' ');
    return (
        <>
            <Kopf
                icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                titel={`Freigegeben${fassung ? `: ${fassung}` : ''}`}
                zeile={unterzeile ? `Freigegeben ${unterzeile}.` : undefined}
            />
            <dl className="mt-4 space-y-2 text-sm">
                <div>
                    <dt className="text-[11px] font-black uppercase tracking-wider text-slate-400">Projektablage</dt>
                    <dd className="mt-0.5 text-slate-700">
                        {ablageLaeuft
                            ? 'Wird gerade festgehalten und abgelegt …'
                            : dokumente.map(d => `${d.titel}: ${d.abgelegt ? 'abgelegt' : 'nicht abgelegt'}`).join(' · ') || 'keine Angabe'}
                        {ablageMeldung && !ablageLaeuft && (
                            <span className={`block text-xs ${TON[ablageMeldung.ton]}`}>{ablageMeldung.text}</span>
                        )}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] font-black uppercase tracking-wider text-slate-400">Abrechnung</dt>
                    <dd className="mt-0.5 text-slate-700">
                        {gebuchtAm ? `Leistung gebucht am ${tagDeutsch(gebuchtAm)}` : 'Leistung noch nicht gebucht'}
                        {buchungsMeldung && <span className={`block text-xs ${TON[buchungsMeldung.ton]}`}>{buchungsMeldung.text}</span>}
                    </dd>
                </div>
            </dl>
            <p className="mt-4 text-xs text-slate-500 leading-snug">
                Die Fassung ist eingefroren. Ändert sich die Planung, fragt der Hinweis oben nach einer neuen Fassung.
            </p>
            {!nurAnsicht && (onNeueFassung || onBuchen) && (
                <Handlungen>
                    {onBuchen && (
                        <button type="button" onClick={() => onBuchen()} disabled={!!sperre} className={KNOPF_LEISE}>
                            Leistung buchen
                        </button>
                    )}
                    {onNeueFassung && (
                        <button type="button" onClick={() => onNeueFassung()} disabled={!!sperre} title={sperre ?? undefined} className={KNOPF_LEISE}>
                            Neue Fassung eröffnen
                        </button>
                    )}
                </Handlungen>
            )}
        </>
    );
};

/** Nur-Ansicht im Entwurf: der Stand, keine Knoepfe. */
export const NurAnsichtKarte: React.FC<NurAnsichtKarteProps> = ({ zusammen }) => (
    <Kopf
        titel="Entwurf (nur zur Ansicht)"
        zeile={`${standSatz(zusammen)}${zusammen.offenePunkte ? `, ${anzahl(zusammen.offenePunkte, 'offener Punkt', 'offene Punkte')}` : ''}.`}
    />
);

/** Laden: ein Satz mit Kreisel. */
export const LadenKarte: React.FC = () => <Kopf rolle="status" icon={<Kreisel />} titel="Wird geladen …" />;
